import { useEffect, useMemo, useRef, useState } from "react";
import { X, Plus, Search, ChevronUp, Lock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * StepResponsibleSelect — sélecteur MULTI d'utilisateurs responsables d'une
 * étape de workflow document.
 *
 * Spec iter118a (Feb 2026) — chaque étape peut avoir 0..N responsables.
 * Tous les utilisateurs listés reçoivent la même notification
 * (`doc_step_action_required`, `doc_created_action_required`,
 * `doc_step_stuck`). Aucun ordre de priorité (l'ordre d'ajout est
 * conservé purement pour l'affichage).
 *
 * Props :
 *   values   : string[]                    — liste d'user ids (canonique)
 *   onChange : (ids: string[]) => void     — appelé à chaque add/remove
 *   testId   : string                       — préfixe data-testid
 */
export default function StepResponsibleSelect({ values, onChange, testId = "step-responsible" }) {
  const { user: currentUser } = useAuth() || {};
  const viewerIsFirstAdmin = !!currentUser?.is_first_admin;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  // Spec iter118b (Feb 2026, fix testing agent LOW) — on garde DEUX listes :
  //   • `users` (active) → alimente le picker (choix de nouveaux responsables)
  //   • `allUsers` (actifs + inactifs, hors is_deleted) → résout les chips
  //     de responsables déjà persistés qui pointeraient sur un utilisateur
  //     non-actif (sinon le chip affichait l'UUID brut).
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/users/directory");
        const items = (data?.items || data || []).filter((u) => !u.is_deleted);
        if (mounted) {
          setAllUsers(items);
          setUsers(items.filter((u) => u.status === "active"));
        }
      } catch {
        // ignore — user will see empty list
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Close picker on outside click.
  useEffect(() => {
    if (!pickerOpen) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setPickerOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [pickerOpen]);

  const valList = useMemo(() => (Array.isArray(values) ? values : []), [values]);
  // Spec iter118b — la résolution nom/initiales des chips utilise
  // `allUsers` (inclut les inactifs) pour éviter d'afficher un UUID brut
  // sur un chip pointant vers un utilisateur devenu inactif.
  const usersById = useMemo(() => {
    const m = {};
    for (const u of allUsers) m[u.id] = u;
    return m;
  }, [allUsers]);

  const availableUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => !valList.includes(u.id))
      .filter((u) => {
        if (!q) return true;
        const nm = `${u.first_name || ""} ${u.last_name || ""} ${u.email || ""}`.toLowerCase();
        return nm.includes(q);
      })
      .sort((a, b) =>
        `${a.first_name || ""} ${a.last_name || ""}`.localeCompare(
          `${b.first_name || ""} ${b.last_name || ""}`,
          "fr",
        ),
      );
  }, [users, valList, search]);

  const addUser = (uid) => {
    if (valList.includes(uid)) return;
    onChange([...valList, uid]);
    setSearch("");
  };

  const removeUser = (uid) => {
    onChange(valList.filter((x) => x !== uid));
  };

  const nameOf = (uid) => {
    const u = usersById[uid];
    if (!u) return uid;
    return (`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || uid);
  };

  const initialsOf = (uid) => {
    const u = usersById[uid];
    if (!u) return "?";
    const first = (u.first_name || "").trim();
    const last = (u.last_name || "").trim();
    return ((first[0] || "") + (last[0] || "")).toUpperCase() || (u.email || "?")[0].toUpperCase();
  };

  // Spec iter121 pt.5 (Feb 2026) — cadenas discret sur le chip quand
  // l'utilisateur est Premier Admin. Verrou informatif : signale
  // visuellement que la LISTE est bloquée par le backend
  // (assert_first_admin_lock) tant que le Premier Admin y figure.
  const isFirstAdminUid = (uid) => !!usersById[uid]?.is_first_admin;

  return (
    <div ref={wrapRef} className="flex flex-col gap-1 flex-1 min-w-0 relative">
      <div className="flex items-start gap-2">
        <div
          className="flex flex-wrap items-center gap-1 min-h-8 px-1.5 py-1 rounded-md border flex-1 min-w-0"
          style={{
            background: "#1B1B1B",
            borderColor: "#2A2A2A",
          }}
          data-testid={testId}
        >
          {valList.length === 0 && (
            <span className="text-xs italic pl-1" style={{ color: "#9a9a95" }}>
              Aucun responsable
            </span>
          )}
          {valList.map((uid) => {
            const isFA = isFirstAdminUid(uid);
            // Spec iter121 pt.6 (Feb 2026) — un Admin secondaire ne peut
            // PAS retirer le Premier Admin (backend renvoie 403). On
            // désactive le bouton × côté client pour éviter le clic
            // qui échoue silencieusement.
            const removeDisabled = isFA && !viewerIsFirstAdmin;
            return (
            <span
              key={uid}
              className="inline-flex items-center gap-1 text-[11px] rounded-full pl-0.5 pr-1 py-0.5"
              style={{
                background: "#141414",
                // Spec iter121 pt.5 — bordure ambre subtile quand la
                // liste est verrouillée par le Premier Admin.
                border: isFA ? "1px solid #D4A056" : "1px solid #5b5fc7",
                color: "#EFEFEF",
              }}
              data-testid={`${testId}-chip-${uid}`}
              data-first-admin={isFA ? "true" : undefined}
              title={isFA
                ? `${nameOf(uid)} · Premier Admin — seul lui peut modifier cette liste`
                : nameOf(uid)}
            >
              <span
                className="inline-flex items-center justify-center rounded-full text-[9px] font-bold"
                style={{
                  width: 16, height: 16,
                  background: isFA ? "#D4A056" : "#5b5fc7",
                  color: "#FFFFFF",
                }}
              >
                {initialsOf(uid)}
              </span>
              {isFA && (
                <Lock
                  size={10}
                  strokeWidth={2.4}
                  style={{ color: "#D4A056" }}
                  data-testid={`${testId}-lock-${uid}`}
                  aria-label="Verrou Premier Admin"
                />
              )}
              <span className="truncate max-w-[110px]">{nameOf(uid)}</span>
              {!removeDisabled && (
              <button
                type="button"
                onClick={() => removeUser(uid)}
                className="ml-0.5 rounded-full p-0.5 transition hover:bg-white/10"
                title={isFA
                  ? "Retirer le Premier Admin (vous êtes Premier Admin, autorisé)"
                  : "Retirer ce responsable"}
                data-testid={`${testId}-remove-${uid}`}
              >
                <X size={10} />
              </button>
              )}
            </span>
            );
          })}
          <button
            type="button"
            disabled={loading}
            onClick={() => setPickerOpen((v) => !v)}
            className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-full hover:bg-white/5 transition ml-auto"
            style={{ color: "#9a9a95" }}
            data-testid={`${testId}-add-btn`}
            title="Ajouter un responsable"
          >
            <Plus size={11} /> Ajouter
          </button>
        </div>
      </div>

      {pickerOpen && (
        <div
          className="absolute z-30 top-full left-6 right-0 mt-1 rounded-md overflow-hidden shadow-2xl"
          style={{ background: "#141414", border: "1px solid #2A2A2A" }}
          data-testid={`${testId}-picker`}
        >
          <div
            className="flex items-center gap-2 px-2.5 py-2"
            style={{ borderBottom: "1px solid #2A2A2A", background: "#1A1A1A" }}
          >
            <Search size={12} style={{ color: "#6B7280" }} />
            <input
              autoFocus
              type="text"
              placeholder="Rechercher un utilisateur…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 h-6 bg-transparent focus:outline-none text-[12px]"
              style={{ color: "#EDEDED" }}
              data-testid={`${testId}-picker-search`}
            />
            <button
              type="button"
              onClick={() => { setPickerOpen(false); setSearch(""); }}
              className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded hover:bg-white/10 flex items-center gap-0.5"
              style={{ color: "#9CA3AF" }}
              data-testid={`${testId}-picker-close`}
            >
              Fermer <ChevronUp size={11} />
            </button>
          </div>
          <div className="max-h-[220px] overflow-y-auto py-1">
            {availableUsers.length === 0 ? (
              <div className="px-3 py-3 text-[11.5px] italic text-center" style={{ color: "#8B8F94" }}>
                {search.trim() ? "Aucun résultat" : "Tous les utilisateurs sont déjà responsables."}
              </div>
            ) : (
              availableUsers.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => addUser(u.id)}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12px] hover:bg-white/5 transition"
                  data-testid={`${testId}-picker-item-${u.id}`}
                >
                  <span
                    className="inline-flex items-center justify-center rounded-full text-[10px] font-bold"
                    style={{ width: 20, height: 20, background: "#3A3A3A", color: "#FFFFFF" }}
                  >
                    {initialsOf(u.id)}
                  </span>
                  <span className="truncate text-white">
                    {(`${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email)}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {valList.length === 0 && (
        <div className="text-[10px] text-muted-foreground italic pl-6">
          Le créateur du document sera utilisé par défaut.
        </div>
      )}
    </div>
  );
}
